/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Wordlist registry. Lazy-loads per-language modules on demand via
 * dynamic import so the initial bundle stays small (each list is
 * ~50–182 KB — Tamil is the outlier because Tamil-script codepoints
 * are 3 bytes each in UTF-8; bundling all 14 up-front would be
 * ~1.0 MB of strings).
 *
 * F10a shipped English; #71 added German; the batch covering #72-#80 +
 * #85-#88 brought the sourced set to 14. Klingon (`kl`, originally
 * filed as #76) was dropped from the supported language set — the
 * curated TKD + Star-Trek source mix has no compatible licence path
 * and the product does not need it.
 *
 * #245 added the quality-verification layer: every list is pinned
 * against measured prefix-collision and edit-distance baselines
 * (`tests/unit/wordlists.quality.test.js`) and covered by fast-check
 * property tests (`tests/unit/properties/wordlists.properties.test.js`).
 * Metrics + methodology: `src/wordlists/README.md` ("Quality
 * verification").
 */

import { defaultRandom, type Random } from '../services/random.js';

export type LanguageCode =
  | 'en'
  | 'de'
  | 'fr'
  | 'it'
  | 'es'
  | 'sv'
  | 'pl'
  | 'zh-cn'
  | 'zh-tw'
  | 'ja'
  | 'ms'
  | 'ta'
  | 'tl'
  | 'pt';

export interface LanguageDescriptor {
  code: LanguageCode;
  /** Display name in the language selector UI. */
  name: string;
  /** Whether the 7776-entry list has been sourced + license-reviewed. */
  isSourced: boolean;
  /** When unsourced, the GitHub follow-up issue number that owns it. */
  followUpIssue?: number;
  /** Tooltip shown when the option is disabled in the UI. */
  unavailableNote?: string;
}

/**
 * Catalogue of every language with a sourced 7776-entry list.
 * Order matches the language-selector UI: the original 10 European/CJK
 * codes first, then the four Singapore/Asia-Pacific additions from
 * issues #85-#88.
 */
export const LANGUAGES: readonly LanguageDescriptor[] = Object.freeze([
  { code: 'en', name: 'English', isSourced: true },
  { code: 'de', name: 'Deutsch', isSourced: true },
  { code: 'fr', name: 'Français', isSourced: true },
  { code: 'it', name: 'Italiano', isSourced: true },
  { code: 'es', name: 'Español', isSourced: true },
  { code: 'sv', name: 'Svenska', isSourced: true },
  { code: 'pl', name: 'Polski', isSourced: true },
  { code: 'zh-cn', name: '简体中文', isSourced: true },
  { code: 'zh-tw', name: '繁體中文', isSourced: true },
  { code: 'ja', name: '日本語', isSourced: true },
  { code: 'ms', name: 'Bahasa Melayu', isSourced: true },
  { code: 'ta', name: 'தமிழ்', isSourced: true },
  { code: 'tl', name: 'Tagalog', isSourced: true },
  { code: 'pt', name: 'Português', isSourced: true },
]);

/**
 * The full ordered language list shown in the UI. Every entry is
 * sourced; `ALL_LANGUAGES === LANGUAGES` until a future placeholder is
 * filed. Kept as a separate export so consumers don't need to switch
 * names if/when placeholders return.
 */
export const ALL_LANGUAGES: readonly LanguageDescriptor[] = LANGUAGES;

const sourcedByCode = new Map<LanguageCode, () => Promise<readonly string[]>>();
sourcedByCode.set('en', async () => (await import('./en.js')).englishWordlist);
sourcedByCode.set('de', async () => (await import('./de.js')).germanWordlist);
sourcedByCode.set('fr', async () => (await import('./fr.js')).frenchWordlist);
sourcedByCode.set('it', async () => (await import('./it.js')).italianWordlist);
sourcedByCode.set('es', async () => (await import('./es.js')).spanishWordlist);
sourcedByCode.set('sv', async () => (await import('./sv.js')).swedishWordlist);
sourcedByCode.set('pl', async () => (await import('./pl.js')).polishWordlist);
sourcedByCode.set(
  'zh-cn',
  async () => (await import('./zh-cn.js')).simplifiedChineseWordlist
);
sourcedByCode.set(
  'zh-tw',
  async () => (await import('./zh-tw.js')).traditionalChineseWordlist
);
sourcedByCode.set('ja', async () => (await import('./ja.js')).japaneseWordlist);
sourcedByCode.set('ms', async () => (await import('./ms.js')).malayWordlist);
sourcedByCode.set('ta', async () => (await import('./ta.js')).tamilWordlist);
sourcedByCode.set('tl', async () => (await import('./tl.js')).tagalogWordlist);
sourcedByCode.set(
  'pt',
  async () => (await import('./pt.js')).portugueseWordlist
);

/**
 * Thrown when a caller asks for a language whose 7776-entry list has
 * not yet been sourced. The UI should never reach this — disabled
 * options + tooltips prevent it — but the API throws loudly if a caller
 * bypasses the UI affordance.
 */
export class UnsourcedLanguageError extends Error {
  readonly code: LanguageCode;
  constructor(code: LanguageCode) {
    const lang = ALL_LANGUAGES.find(l => l.code === code);
    super(
      `wordlist for '${code}' (${lang?.name ?? '?'}) is not yet sourced; see ${
        lang?.followUpIssue ? `#${lang.followUpIssue}` : 'follow-up issue'
      }`
    );
    this.name = 'UnsourcedLanguageError';
    this.code = code;
  }
}

/**
 * Load and return the wordlist for the supplied language. Each list has
 * exactly 7776 unique entries. Lazy: the module is dynamically imported
 * the first time a language is used, then served from the bundler's
 * module cache for subsequent calls.
 *
 * @throws {UnsourcedLanguageError} if the language hasn't been sourced.
 */
export const loadWordlist = async (
  code: LanguageCode
): Promise<readonly string[]> => {
  const loader = sourcedByCode.get(code);
  if (!loader) {
    throw new UnsourcedLanguageError(code);
  }
  return loader();
};

/**
 * Pick a single word from the supplied list using the F3 RNG seam.
 * Uses rejection sampling to keep the distribution exactly uniform
 * across `wordlist.length` regardless of the modulus boundary.
 */
export const pickRandomWord = (
  wordlist: readonly string[],
  rng: Random = defaultRandom
): string => {
  if (wordlist.length === 0) {
    throw new Error('cannot pick from an empty wordlist');
  }
  // Uniform sampling: reject draws that fall in the unused tail of
  // 2^32 modulo wordlist.length so every entry has equal probability.
  const range = wordlist.length;
  const limit = Math.floor(0x100000000 / range) * range;
  while (true) {
    const bytes = rng.getRandomBytes(4);
    const draw =
      ((bytes[0] ?? 0) << 24) |
      ((bytes[1] ?? 0) << 16) |
      ((bytes[2] ?? 0) << 8) |
      (bytes[3] ?? 0);
    // Convert signed 32-bit to unsigned for comparison.
    const unsigned = draw >>> 0;
    if (unsigned < limit) {
      return wordlist[unsigned % range] as string;
    }
  }
};
